import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';

export interface PlatformInfo {
  isNative: boolean;
  platform: 'web' | 'ios' | 'android';
  isMobile: boolean;
  deviceInfo?: {
    model: string;
    platform: string;
    operatingSystem: string;
    osVersion: string;
    manufacturer: string;
    isVirtual: boolean;
    webViewVersion: string;
  };
}

let cachedPlatformInfo: PlatformInfo | null = null;

export async function getPlatformInfo(): Promise<PlatformInfo> {
  if (cachedPlatformInfo) {
    return cachedPlatformInfo;
  }

  const isNative = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform() as 'web' | 'ios' | 'android';
  const isMobile = platform === 'ios' || platform === 'android';

  let deviceInfo;
  if (isNative) {
    try {
      const info = await Device.getInfo();
      deviceInfo = {
        model: info.model,
        platform: info.platform,
        operatingSystem: info.operatingSystem,
        osVersion: info.osVersion,
        manufacturer: info.manufacturer,
        isVirtual: info.isVirtual,
        webViewVersion: info.webViewVersion,
      };
    } catch (error) {
      console.warn('Failed to get device info:', error);
    }
  }

  cachedPlatformInfo = {
    isNative,
    platform,
    isMobile,
    deviceInfo,
  };

  return cachedPlatformInfo;
}

export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

export function isWebApp(): boolean {
  return !Capacitor.isNativePlatform();
}

export function getCurrentPlatform(): 'web' | 'ios' | 'android' {
  return Capacitor.getPlatform() as 'web' | 'ios' | 'android';
}

export async function getNetworkStatus() {
  if (isNativeApp()) {
    try {
      return await Network.getStatus();
    } catch (error) {
      console.warn('Failed to get network status:', error);
      return { connected: true, connectionType: 'unknown' };
    }
  }
  return { connected: navigator.onLine, connectionType: 'unknown' };
}
